import { types } from "mobx-state-tree";

const tabKeyModel = types.model("tabKeyModel", {
    id: types.identifier,
    tabKey: types.string
});

const pillKeyModel = types.model("pillKeyModel", {
    id: types.identifier,
    pillKey: types.frozen(types.array(types.string))
});

const toggleKeyModel = types.model("toggleKeyModel", {
    id: types.identifier,
    toggleKey: types.boolean
});

const dataKeyModel = types.model("dataKeyModel", {
    id: types.identifier,
    value: types.string
});

export type SortColumnOption = {
    sortColumn: string | number;
    sortDirection: "ascending" | "descending";
};

export type TableOption = {
    columnWidth?: Record<string, string | undefined>;
    sortDirection?: "ascending" | "descending";
    sortColumn?: string | number;
    sortColumns?: SortColumnOption[];
};

const tableOptionModel = types.model("tableOptionModel", {
    id: types.identifier,
    tableOptions: types.frozen<TableOption>()
});

const regexExp = new RegExp("incidents/\\d+/details", "i");

export const volatileControlSettingStore = types
    .model({
        tabKeys: types.map(tabKeyModel),
        pillKeys: types.map(pillKeyModel),
        dataKeys: types.map(dataKeyModel)
    })
    .views((volatileSelf) => ({
        getTabKey(id: string) {
            return volatileSelf.tabKeys.get(id)?.tabKey;
        },
        getPillKey(id: string): readonly string[] | undefined {
            return volatileSelf.pillKeys.get(id)?.pillKey;
        },
        getDataValue(key: string) {
            return volatileSelf.dataKeys.get(key)?.value;
        }
    }))
    .actions((volatileSelf) => ({
        setPillKey(id: string, pillKey: readonly string[]) {
            volatileSelf.pillKeys.put({ id, pillKey });
        },
        setDataValue(key: string, value: string) {
            volatileSelf.dataKeys.put({ id: key, value });
        },
        setTabKey(id: string, tabKey: string) {
            volatileSelf.tabKeys.put({ id, tabKey });
        }
    }));

export const ControlSettingStore = types
    .model("controlSetting", {
        tabKeys: types.map(tabKeyModel),
        pillKeys: types.map(pillKeyModel),
        toggleKeys: types.map(toggleKeyModel),
        tableOptions: types.map(tableOptionModel),
        dataKeys: types.map(dataKeyModel)
    })
    .volatile((self) => ({
        volatile: volatileControlSettingStore.create()
    }))
    .views((self) => ({
        getTabKey(id: string, volatileOnly = false) {
            if (volatileOnly) {
                return self.volatile.getTabKey(id);
            }
            return self.volatile.getTabKey(id) ?? self.tabKeys.get(id)?.tabKey;
        },
        getPillKey(id: string, volatileOnly = false): readonly string[] | undefined {
            if (regexExp.test(id) || volatileOnly) {
                return self.volatile.getPillKey(id);
            }
            return self.volatile.getPillKey(id) ?? self.pillKeys.get(id)?.pillKey;
        },
        getToggleKey(id: string) {
            return self.toggleKeys.get(id)?.toggleKey;
        },
        getTableOption(id: string): TableOption | undefined {
            return self.tableOptions.get(id)?.tableOptions;
        },
        getDataValue(key: string, volatileOnly = false) {
            if (regexExp.test(key) || volatileOnly) {
                return self.volatile.getDataValue(key);
            }
            return self.volatile.getDataValue(key) ?? self.dataKeys.get(key)?.value;
        }
    }))
    .actions((self) => ({
        setTabKey(id: string, tabKey: string, volatile = false) {
            if (volatile) {
                self.volatile.setTabKey(id, tabKey);
            } else {
                self.tabKeys.put({ id, tabKey });
            }
        },
        setPillKey(id: string, pillKey: readonly string[], volatile = false) {
            if (regexExp.test(id) || volatile) {
                self.volatile.setPillKey(id, pillKey);
            } else {
                self.pillKeys.put({ id, pillKey });
            }
        },
        setToggleKey(id: string, toggleKey: boolean) {
            self.toggleKeys.put({ id, toggleKey });
        },
        setTableOption(id: string, tableOptions: TableOption) {
            const previousTableOptions = self.tableOptions.get(id)?.tableOptions;
            self.tableOptions.put({
                id,
                tableOptions: {
                    columnWidth: {
                        ...previousTableOptions?.columnWidth,
                        ...tableOptions.columnWidth
                    },
                    sortDirection: tableOptions.sortDirection ?? previousTableOptions?.sortDirection,
                    sortColumn: tableOptions.sortColumn ?? previousTableOptions?.sortColumn,
                    sortColumns: tableOptions.sortColumns ?? previousTableOptions?.sortColumns
                }
            });
        },
        setDataValue(key: string, value: string, volatile = false) {
            if (regexExp.test(key) || volatile) {
                self.volatile.setDataValue(key, value);
            } else {
                self.dataKeys.put({ id: key, value });
            }
        },
        deleteDataValue(key: string) {
            self.dataKeys.delete(key);
        }
    }));
